import { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  type SortChangedEvent,
} from 'ag-grid-community';
import { trendzGridTheme } from '@components/grid/trendzGridTheme';
import '@components/grid/agGridTheme.css';
import { buildTagsColumns } from '../columns';
import {
  TAG_SORT_KEYS,
  type SortDirection,
  type Tag,
  type TagSortKey,
} from '../schema';
import type { CommonResponse } from '@/types/api';

ModuleRegistry.registerModules([AllCommunityModule]);

interface Props {
  data: CommonResponse<Tag> | undefined;
  onEdit: (row: Tag) => void;
  onSortChange: (sort: { sortBy?: TagSortKey; sortDir?: SortDirection }) => void;
}

const DEFAULT_COL_DEF = {
  resizable: true,
  sortable: false,
  filter: false,
  minWidth: 100,
  unSortIcon: true,
};

const SORT_KEY_SET = new Set<string>(TAG_SORT_KEYS);

function readSort(event: SortChangedEvent): {
  sortBy?: TagSortKey;
  sortDir?: SortDirection;
} {
  const state = event.api.getColumnState();
  const active = state.find(
    (c) => c.sort != null && c.colId && SORT_KEY_SET.has(c.colId),
  );
  if (!active || !active.sort || !active.colId) return {};
  return {
    sortBy: active.colId as TagSortKey,
    sortDir: active.sort as SortDirection,
  };
}

export function TagsGrid({ data, onEdit, onSortChange }: Props) {
  const columnDefs = useMemo(() => buildTagsColumns({ onEdit }), [onEdit]);

  return (
    <div className="h-full w-full">
      <AgGridReact<Tag>
        theme={trendzGridTheme}
        rowData={data?.rowData ?? []}
        columnDefs={columnDefs}
        defaultColDef={DEFAULT_COL_DEF}
        animateRows
        getRowId={(p) => p.data.id}
        suppressCellFocus
        suppressMultiSort
        onSortChanged={(e) => onSortChange(readSort(e))}
      />
    </div>
  );
}
